import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ApiService } from '../api.service';
import { API_ENDPOINT, APP_CONFIG, LOCAL_STORAGE_KEY, SESSION_STORAGE_KEY } from '@core/configs';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { SessionStorageService } from '../session-storage.service';
import { LocalStorageService } from '../local-storage.service';

/** Maximum number of retries when a Firebase Auth operation fails. */
const MAX_RETRY = 10;

/** Delay between retries (ms). */
const RETRY_DELAY_MS = 500;

/** Refresh custom claims before the Firebase ID token gets too close to expiry. */
const TOKEN_REFRESH_WINDOW_MS = 15 * 60 * 1000;

/**
 * Manages Firebase Authentication: sign in with a backend custom token, sign out,
 * refresh an ID token through the Firebase SDK, and refresh backend custom claims when needed.
 */
@Injectable({ providedIn: 'root' })
export class FirebaseAuthService {

  constructor(
    private readonly http: ApiService,
    private afAuth: AngularFireAuth,
    private sessionStorage: SessionStorageService,
    private localStorage: LocalStorageService
  ) {
    // Track auth state (debug log).
    this.afAuth.authState.subscribe(user => {
      if (!user) {
        console.log('[Firebase] Not logged in');
      }
    });

    // Track token (debug log).
    this.afAuth.idToken.subscribe(token => {
      if (token) {
        console.log('[Firebase] New token, refreshed at:', new Date().toLocaleString());
      } else {
        console.log('[Firebase] Token no longer exists (user may have signed out)');
      }
    });
  }

  /** Sign in to Firebase using a custom token fetched from the backend. */
  public async loginWithFirebaseToken(corpPrefix: string, officeCd: string): Promise<boolean> {
    if (!APP_CONFIG.useFirebase) return false;

    try {
      const res = await this.fetchCustomToken(corpPrefix, officeCd);
      const customToken = res?.result;
      if (!customToken) {
        console.error('[Firebase] Network connection lost. Unable to retrieve Firebase Token from backend.');
        return false;
      }
      return await this.signInWithCustomToken(customToken);
    } catch (error) {
      console.error('[Firebase] Network connection lost. Unable to retrieve Firebase Token from backend.');
      return false;
    }
  }

  /** Sign out from Firebase. */
  public async logoutWithFirebaseToken(): Promise<void> {
    if (!APP_CONFIG.useFirebase) return;
    console.log('[Firebase] logoutWithFirebaseToken_afAuth.signOut');
    return await this.afAuth.signOut();
  }

  /**
   * Fetch a new backend custom token and sign in again. Use this when the backend must
   * recalculate claims (office/permission change) or Firebase SDK token refresh failed.
   */
  public async fireAuthRefreshToken(): Promise<boolean> {
    try {
      // Read directly from sessionStorage to avoid stale in-memory data.
      const officeSelected = this.sessionStorage.getSessionData(SESSION_STORAGE_KEY.officeSelected);
      const corpPrefix = this.localStorage.getItem(LOCAL_STORAGE_KEY.corpPrefix) || '';
      const officeCd = officeSelected?.officeCd;

      if (corpPrefix && officeCd) {
        return await this.loginWithFirebaseToken(corpPrefix, officeCd);
      }
      return false;
    } catch (error) {
      console.error('[Firebase] Token refresh failed:', error);
      return false;
    }
  }

  /** Force Firebase SDK to issue a new ID token without calling the CareMoni backend. */
  public async refreshIdToken(): Promise<boolean> {
    if (!APP_CONFIG.useFirebase) return false;

    try {
      const user = await this.afAuth.currentUser;
      if (!user) return false;
      await user.getIdToken(true);
      return true;
    } catch (error) {
      console.warn('[Firebase] SDK ID token refresh failed:', error);
      return false;
    }
  }

  /** True when there is no signed-in Firebase user, or its ID token expires within the refresh window. */
  public async isIdTokenExpiringSoon(refreshWindowMs = TOKEN_REFRESH_WINDOW_MS): Promise<boolean> {
    if (!APP_CONFIG.useFirebase) return false;

    try {
      const user = await this.afAuth.currentUser;
      if (!user) return true;

      const tokenResult = await user.getIdTokenResult();
      const expiresAt = new Date(tokenResult.expirationTime).getTime();
      return !Number.isFinite(expiresAt) || expiresAt - Date.now() <= refreshWindowMs;
    } catch (error) {
      console.warn('[Firebase] Could not inspect ID token expiry:', error);
      return true;
    }
  }

  /** Sign in with the custom token, retrying up to MAX_RETRY times. */
  private async signInWithCustomToken(customToken: string): Promise<boolean> {
    let lastError: any;
    for (let attempt = 1; attempt <= MAX_RETRY; attempt++) {
      try {
        await this.afAuth.signInWithCustomToken(customToken);
        return true;
      } catch (error) {
        lastError = error;
        console.warn(`[Firebase] Login with custom token failed on attempt ${attempt}:`, error);
        if (attempt < MAX_RETRY) {
          await this.delay(RETRY_DELAY_MS);
        }
      }
    }
    console.error(`[Firebase] Login with custom token failed after ${MAX_RETRY} attempts:`, lastError);
    return false;
  }

  /** Call the backend to fetch a Firebase custom token, retrying up to MAX_RETRY times. */
  private async fetchCustomToken(corpPrefix: string, officeCd: string): Promise<any> {
    if (!APP_CONFIG.useFirebase || !officeCd || !corpPrefix) return '';

    const url = `${API_ENDPOINT.realtimedb.getFirebaseToken}`;
    const body = { 'corpprefix': corpPrefix, 'officecd': officeCd };
    for (let attempt = 1; attempt <= MAX_RETRY; attempt++) {
      try {
        return await lastValueFrom(this.http.post(url, body));
      } catch (error) {
        console.warn(`[Firebase] Failed to get Firebase Token on attempt ${attempt}:`, error);
        if (attempt < MAX_RETRY) {
          await this.delay(RETRY_DELAY_MS);
        }
      }
    }
    return '';
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
